import { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  buildTakeoff,
  COATINGS,
  INSULATION_TYPES,
  JACKETS,
  recommendedThickness,
  SERVICE_BANDS,
  type CoatingId,
  type InsulationType,
  type ServiceBand,
  type TakeoffSettings,
} from "@/lib/pipe-insulation";
import type { MaterialId } from "@/lib/pipe-spec";
import type {
  ComponentPlacement,
  CutPiece,
  RunFitting,
} from "@/hooks/use-pipe-sketch";

interface Props {
  material: MaterialId;
  cutList: CutPiece[];
  fittings: RunFitting[];
  placements: ComponentPlacement[];
  settings: TakeoffSettings;
  onChange: (patch: Partial<TakeoffSettings>) => void;
}

const n1 = (v: number) => v.toFixed(1);

const InsulationPanel = ({
  material,
  cutList,
  fittings,
  placements,
  settings,
  onChange,
}: Props) => {
  const result = useMemo(
    () =>
      buildTakeoff({
        material,
        cutList: cutList.map((c) => ({ size: c.size, cutLength: c.cutLength })),
        fittings: fittings.map((f) => ({ size: f.size, kind: f.kind })),
        placements: placements.map((p) => ({ size: p.size })),
        settings,
      }),
    [material, cutList, fittings, placements, settings],
  );

  const insulated = settings.insulation !== "none";
  const typeMeta = INSULATION_TYPES.find((t) => t.id === settings.insulation);
  const mismatch =
    insulated && typeMeta && !typeMeta.services.includes(settings.service);

  return (
    <div className="space-y-4 p-3">
      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Insulation &amp; coating spec</h3>

        <div className="space-y-1">
          <Label className="text-xs">Insulation</Label>
          <Select
            value={settings.insulation}
            onValueChange={(v) => onChange({ insulation: v as InsulationType })}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INSULATION_TYPES.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {typeMeta && typeMeta.id !== "none" && (
            <p className="text-[11px] text-muted-foreground">{typeMeta.spec}</p>
          )}
        </div>

        {insulated && (
          <>
            <div className="space-y-1">
              <Label className="text-xs">Service</Label>
              <Select
                value={settings.service}
                onValueChange={(v) => onChange({ service: v as ServiceBand })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_BANDS.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {mismatch && (
              <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-[11px]">
                {typeMeta?.label} is not normally specified for this service — check the
                mechanical spec before ordering.
              </p>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Thickness (in)</Label>
                <Input
                  type="number"
                  step="0.25"
                  inputMode="decimal"
                  placeholder="auto"
                  value={settings.thicknessOverride ?? ""}
                  onChange={(e) =>
                    onChange({
                      thicknessOverride: e.target.value === "" ? null : Number(e.target.value),
                    })
                  }
                  className="h-8"
                />
                <p className="text-[11px] text-muted-foreground">
                  Blank = per size table (2&quot;:{" "}
                  {recommendedThickness("2", settings.service)}&quot;)
                </p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Jacket</Label>
                <Select
                  value={settings.jacket}
                  onValueChange={(v) => onChange({ jacket: v as TakeoffSettings["jacket"] })}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {JACKETS.map((j) => (
                      <SelectItem key={j.id} value={j.id}>
                        {j.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </>
        )}

        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Coating</Label>
            <Select
              value={settings.coating}
              onValueChange={(v) => onChange({ coating: v as CoatingId })}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COATINGS.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">{result.coating.note}</p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Waste allowance (%)</Label>
            <Input
              type="number"
              inputMode="decimal"
              value={settings.wastePct}
              onChange={(e) => onChange({ wastePct: Number(e.target.value) || 0 })}
              className="h-8"
            />
          </div>
        </div>
      </section>

      <section className="space-y-2 border-t pt-3">
        <h3 className="text-sm font-semibold">Takeoff</h3>
        {result.lines.length === 0 ? (
          <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
            Draw some pipe and the covering areas land here.
          </p>
        ) : (
          <>
            <div className="overflow-hidden rounded-md border">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 text-[11px] uppercase text-muted-foreground">
                  <tr>
                    <th className="p-2 text-left">Size</th>
                    <th className="p-2 text-right">Ft</th>
                    <th className="p-2 text-right">Fitting eq</th>
                    <th className="p-2 text-right">Thk</th>
                    <th className="p-2 text-right">Sq ft</th>
                  </tr>
                </thead>
                <tbody>
                  {result.lines.map((l) => (
                    <tr key={l.size} className="border-t">
                      <td className="p-2 font-medium">{l.size}</td>
                      <td className="p-2 text-right">{n1(l.feet)}</td>
                      <td className="p-2 text-right text-muted-foreground">
                        {n1(l.equivFeet)}
                      </td>
                      <td className="p-2 text-right">
                        {insulated ? `${l.thickness}"` : "—"}
                      </td>
                      <td className="p-2 text-right">
                        {n1(insulated ? l.insulArea : l.coatArea)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <dl className="grid grid-cols-2 gap-2 text-xs">
              {insulated && (
                <>
                  <div className="rounded-md border p-2">
                    <dt className="text-[11px] uppercase text-muted-foreground">
                      Insulation area
                    </dt>
                    <dd className="text-sm font-semibold">{n1(result.insulArea)} sq ft</dd>
                  </div>
                  <div className="rounded-md border p-2">
                    <dt className="text-[11px] uppercase text-muted-foreground">Jacket</dt>
                    <dd className="text-sm font-semibold">{n1(result.jacketArea)} sq ft</dd>
                  </div>
                  <div className="rounded-md border p-2">
                    <dt className="text-[11px] uppercase text-muted-foreground">
                      Fitting covers
                    </dt>
                    <dd className="text-sm font-semibold">
                      {result.fittingCovers + result.valveCovers} ea
                    </dd>
                  </div>
                  <div className="rounded-md border p-2">
                    <dt className="text-[11px] uppercase text-muted-foreground">
                      Insulation weight
                    </dt>
                    <dd className="text-sm font-semibold">{n1(result.insulWeight)} lb</dd>
                  </div>
                </>
              )}
              {result.coating.coats > 0 && (
                <>
                  <div className="rounded-md border p-2">
                    <dt className="text-[11px] uppercase text-muted-foreground">
                      Painted area
                    </dt>
                    <dd className="text-sm font-semibold">{n1(result.coatArea)} sq ft</dd>
                  </div>
                  <div className="rounded-md border p-2">
                    <dt className="text-[11px] uppercase text-muted-foreground">
                      Coating ({result.coating.coats} coat
                      {result.coating.coats > 1 ? "s" : ""})
                    </dt>
                    <dd className="text-sm font-semibold">
                      {result.coatGallons.toFixed(2)} gal
                    </dd>
                  </div>
                </>
              )}
            </dl>
            <p className="text-[11px] text-muted-foreground">
              Areas are measured on the outer face of the covering, with fittings and valves
              converted to equivalent feet the way an insulator takes them off.
            </p>
          </>
        )}
      </section>
    </div>
  );
};

export default InsulationPanel;
